/**
 * ProsPix PXL runtime entrypoint.
 * - Resolves config, consent, and IDs
 * - Drains loader queue safely
 * - Loads plugins and flushes events in the background
 */
import manifestFallback from "../config/manifest.json";
import { readAttributionSnapshot } from "../lib/attribution";
import { readStoredConsent, resolveConsent } from "../lib/consent";
import { buildConsentEvidence } from "../lib/consent-evidence";
import { buildContext } from "../lib/context";
import { createEventBus } from "../lib/event-bus";
import { createIdManager } from "../lib/ids";
import {
  normalizeDecision,
  normalizeExposure,
  normalizeIdentityResolution,
  normalizeOutcome,
  type DecisionInput,
  type ExposureInput,
  type IdentityResolutionInput,
  type OutcomeInput,
} from "../lib/intelligence-api";
import { createPageLifecycleEventGate } from "../lib/lifecycle";
import { createLogger } from "../lib/logger";
import { canAcceptProvider, getPlatformStatus } from "../lib/platform";
import { createQueue } from "../lib/queue";
import { createRedactor } from "../lib/redact";
import { createStorageAdapters } from "../lib/storage";
import { postJson, postWithRetry } from "../lib/transport";
import type {
  ConsentSnapshot,
  DataCategory,
  FirstPartyIdentityCapture,
  PixelConfig,
  PixelFlags,
  PluginFactory,
  PluginInstance,
  PluginManifest,
  TrackEvent,
} from "../lib/types";
import { ulid } from "../lib/ulid";
import { now, safeDocument, safeWindow } from "../lib/utils";

import {
  deepMerge,
  loadDefaults,
  loadFlags,
  loadJson,
  loadRemoteConfig,
  resolveConfig,
} from "./config";

const GLOBAL_GUARD = "__PROSPIX_PIXEL_V1__";
const PLUGIN_REGISTER = "__PROSPIX_PLUGIN_REGISTER__";

function findSelfScript(): HTMLScriptElement | null {
  if (typeof document === "undefined") return null;
  if (
    document.currentScript &&
    document.currentScript instanceof HTMLScriptElement
  ) {
    return document.currentScript;
  }
  const scripts = document.getElementsByTagName("script");
  for (let i = scripts.length - 1; i >= 0; i -= 1) {
    const src = scripts[i].src || "";
    if (src.includes("/cdn/pxl-loader.js") || src.includes("/cdn/v1/pxl.js")) {
      return scripts[i];
    }
  }
  return scripts.length > 0 ? scripts[scripts.length - 1] : null;
}

function resolveScriptUrl(): URL {
  const script = findSelfScript();
  if (script?.src) return new URL(script.src, window.location.href);
  return new URL(window.location.href);
}

function loadManifest(scriptUrl: URL): Promise<PluginManifest> {
  const fallback = manifestFallback as PluginManifest;
  const url = new URL(
    "/cdn/v1/manifests/plugin-manifest.json",
    scriptUrl.origin,
  );
  return loadJson(url.toString(), fallback);
}

function isEssentialEvent(type: string): boolean {
  return (
    type === "consent_update" ||
    type === "diagnostic" ||
    type === "pixel_error" ||
    type === "traffic_presence" ||
    type === "bot_signal"
  );
}

function applyFeatureFlags(
  config: PixelConfig,
  features: Record<string, boolean>,
): PixelConfig {
  if (!features || !Object.keys(features).length) return config;
  const disabled = new Set(config.plugins.disabled ?? []);
  const disable = (name?: string) => {
    if (name) disabled.add(name);
  };
  const isFalse = (key: string) => features[key] === false;

  if (isFalse("engagement")) disable("engagement");
  if (isFalse("scrollDepth")) disable("scroll");
  if (isFalse("downloads")) disable("download");
  if (isFalse("forms")) disable("forms");
  if (isFalse("ecommerce")) disable("ecommerce");
  if (isFalse("video")) disable("video");
  if (isFalse("heatmap")) disable("heatmap");
  if (isFalse("replay")) disable("replay");
  if (isFalse("webVitals")) disable("webvitals");
  if (isFalse("performance")) disable("perf");
  if (isFalse("errors")) disable("errors");
  if (isFalse("bots")) disable("bot");
  if (isFalse("adblock")) disable("adblock");
  if (isFalse("sessionQuality")) disable("session_quality");
  if (isFalse("experiments")) disable("experiments");
  if (isFalse("identityLinkage")) disable("identitylink");
  if (isFalse("identityLinkage")) disable("identitystorage");
  if (isFalse("intentScoring")) disable("intent");
  if (isFalse("journeyAbandonment")) disable("journey");
  if (isFalse("search")) disable("search");
  if (isFalse("impressions")) disable("impressions");
  if (isFalse("firstPartyCookies")) disable("firstparty");
  if (isFalse("pageView")) disable("router");

  const clicksDisabled = isFalse("clicks");
  const ctasDisabled = isFalse("ctas");
  if (clicksDisabled && ctasDisabled) disable("click");

  return {
    ...config,
    features,
    plugins: {
      ...config.plugins,
      disabled: Array.from(disabled),
    },
  };
}

function normalizeConsentMode(mode?: string): PixelConfig["consentMode"] {
  if (!mode) return undefined;
  if (mode === "opt-out" || mode === "opt-in" || mode === "strict") return mode;
  return undefined;
}

const DATA_CATEGORY_KINDS: Record<
  DataCategory,
  "essential" | "analytics" | "marketing"
> = {
  context_page: "analytics",
  context_device: "analytics",
  marketing_params: "marketing",
  network_hints: "analytics",
  behavioral_events: "analytics",
  form_analytics: "analytics",
  commerce_analytics: "marketing",
  video_analytics: "analytics",
  performance_analytics: "analytics",
  error_telemetry: "essential",
  bot_signals: "essential",
  attribution: "marketing",
  outbound_clicks: "marketing",
  session_quality: "analytics",
  search_intent: "marketing",
  impression_analytics: "analytics",
  frustration_signals: "analytics",
  experiment_exposure: "analytics",
  identity_linkage: "marketing",
  identity_resolution: "marketing",
  audience_decisioning: "marketing",
  heatmap: "analytics",
  replay: "analytics",
  adblock_signals: "analytics",
  geo_enrichment: "analytics",
  third_party_destinations: "marketing",
  consent_analytics: "essential",
};

const DATA_CATEGORIES = Object.keys(DATA_CATEGORY_KINDS) as DataCategory[];

(function bootstrap() {
  try {
    const win = safeWindow();
    if (!win) return;
    if ((win as any)[GLOBAL_GUARD]) return;
    (win as any)[GLOBAL_GUARD] = true;

    const scriptUrl = resolveScriptUrl();
    const storage = createStorageAdapters();

    let config = resolveConfig(scriptUrl);
    config.consentMode = normalizeConsentMode(config.consentMode);

    const logger = createLogger(config.debug);

    const consentKey = `prospix_consent_${config.siteKey || "default"}`;
    const consentEvidenceKey = `prospix_consent_evidence_${config.siteKey || "default"}`;
    const readConsentFromStorage = () =>
      readStoredConsent(storage.local.get(consentKey));

    let consentSnapshot: ConsentSnapshot = {
      state: readConsentFromStorage()?.state ?? config.privacy.defaultConsent,
      gpc: false,
      dnt: false,
    };

    let flags: PixelFlags = {};
    let manifest: PluginManifest = manifestFallback as PluginManifest;

    const bus = createEventBus<{
      track: TrackEvent;
      consent: ConsentSnapshot;
    }>();

    let redactor = createRedactor({
      redactKeys: config.privacy.redactKeys,
      allowedQueryParams: config.privacy.allowedQueryParams,
    });

    const idManager = createIdManager(storage.local, storage.cookies, {
      sessionTtlMs: 30 * 60 * 1000,
    });

    let queue = createQueue(storage.local, {
      key: `prospix_queue_${config.siteKey || "default"}`,
      maxEvents: config.queue.maxEvents,
      maxBytes: config.queue.maxBytes,
      ttlMs: config.queue.ttlMs,
    });
    const pageLifecycleEventGate = createPageLifecycleEventGate();

    const pendingPluginLoads = new Map<
      string,
      { promise: Promise<void>; resolve: () => void }
    >();
    const pluginFactories = new Map<string, PluginFactory>();
    const pluginInstances = new Map<string, PluginInstance>();

    (win as any)[PLUGIN_REGISTER] = (name: string, factory: PluginFactory) => {
      pluginFactories.set(name, factory);
      const pending = pendingPluginLoads.get(name);
      if (pending) {
        pending.resolve();
        pendingPluginLoads.delete(name);
      }
    };

    const getIds = () => idManager.getIds();

    const isUspSaleOptOut = () => {
      const uspString = consentSnapshot.cmp?.uspString;
      if (typeof uspString !== "string") return false;
      if (uspString.length < 3) return false;
      // IAB US Privacy string: "1" + Notice + OptOutSale + LspaCovered
      if (uspString[0] !== "1") return false;
      return uspString[2]?.toUpperCase() === "Y";
    };

    // Central consent gate used by runtime and plugins.
    const resolveConsentMode = () => {
      const region = config.privacy.region;
      const regional = region
        ? config.privacy.regionConsentModes?.[region]
        : undefined;
      return normalizeConsentMode(regional ?? config.consentMode);
    };

    const canTrack = (
      kind: "essential" | "marketing" | "analytics" = "analytics",
    ) => {
      if (storage.local.get("prospix_optout") === "1") return false;
      if (kind === "marketing" && isUspSaleOptOut()) return false;
      if (consentSnapshot.state === "granted") return true;
      if (consentSnapshot.state === "denied") return kind === "essential";
      if (resolveConsentMode() === "opt-out") return true;
      return kind === "essential";
    };

    const resolveCategoryMode = (category: DataCategory) => {
      const region = config.privacy.region;
      const regionOverride = region
        ? config.privacy.regionDataCategories?.[region]?.[category]
        : undefined;
      const globalMode = config.privacy.dataCategories?.[category];
      const mode = regionOverride ?? globalMode ?? "consented";
      if (mode === "off" || mode === "always" || mode === "consented") {
        return mode;
      }
      return "consented";
    };

    const canTrackCategory = (category: DataCategory) => {
      const mode = resolveCategoryMode(category);
      if (mode === "off") return false;
      if (mode === "always") return canTrack("essential");
      const kind = DATA_CATEGORY_KINDS[category] ?? "analytics";
      return canTrack(kind);
    };

    const buildCategoryPolicySnapshot = () => {
      const snapshot: Record<string, boolean> = {};
      for (const category of DATA_CATEGORIES) {
        snapshot[category] = canTrackCategory(category);
      }
      return snapshot;
    };

    let warnedMissingSiteKey = false;
    const trackEvent = (type: string, props?: Record<string, unknown>) => {
      if (flags.killSwitch) return;
      if (!config.siteKey) {
        if (!warnedMissingSiteKey) {
          logger.warn("missing_site_key");
          warnedMissingSiteKey = true;
        }
        return;
      }
      const essential = isEssentialEvent(type);
      if (!canTrack(essential ? "essential" : "analytics")) return;
      if (!essential && Math.random() > config.plugins.sampling.rate) return;
      if (!pageLifecycleEventGate.shouldTrack(type, config.siteKey)) return;

      if (!essential) {
        // Ensure stable visitor/session IDs for queued analytics events.
        getIds();
      }

      // Redact and sample before enqueueing to avoid persisting sensitive data.
      const attribution =
        !essential &&
        type !== "attribution_update" &&
        canTrackCategory("attribution")
          ? readAttributionSnapshot(storage.local)
          : undefined;
      const propsWithAttribution = attribution
        ? { ...(props ?? {}), attribution }
        : props;
      const sanitizedProps =
        propsWithAttribution && typeof propsWithAttribution === "object"
          ? type === "replay_chunk"
            ? (propsWithAttribution as Record<string, unknown>)
            : redactor.sanitizeObject(propsWithAttribution)
          : undefined;
      const event: TrackEvent = {
        eventId: ulid(),
        type,
        occurredAt: now(),
        props: sanitizedProps,
      };

      queue.enqueue({
        eventId: event.eventId,
        type: event.type,
        occurredAt: event.occurredAt,
        props: event.props,
        enqueuedAt: now(),
        attempts: 0,
      });

      bus.emit("track", event);
    };

    const captureIdentity = async (
      capture: FirstPartyIdentityCapture,
    ): Promise<boolean> => {
      if (!config.identityCapture.enabled || flags.killSwitch) return false;
      if (!config.siteKey || storage.local.get("prospix_optout") === "1") {
        return false;
      }
      if (!canTrack("marketing")) return false;
      if (!canTrackCategory("identity_linkage")) return false;
      if (
        consentSnapshot.state !== "granted" ||
        consentSnapshot.gpc ||
        consentSnapshot.dnt
      ) {
        return false;
      }

      const normalize = (value: unknown, max: number) =>
        typeof value === "string" && value.trim()
          ? value.trim().slice(0, max)
          : undefined;
      const normalizedEmail = normalize(capture.email, 320)?.toLowerCase();
      const email =
        normalizedEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)
          ? normalizedEmail
          : undefined;
      const phone = normalize(capture.phone, 40);
      const externalId = normalize(capture.externalId, 256);
      const profile = capture.profile
        ? {
            first: normalize(capture.profile.first, 100),
            middle: normalize(capture.profile.middle, 100),
            last: normalize(capture.profile.last, 100),
            street: normalize(capture.profile.street, 200),
            city: normalize(capture.profile.city, 120),
            state: normalize(capture.profile.state, 120),
            postalCode: normalize(capture.profile.postalCode, 32),
            country: normalize(capture.profile.country, 64),
          }
        : undefined;
      const hasProfile = Boolean(
        profile && Object.values(profile).some(Boolean),
      );
      if (!email && !phone && !externalId && !hasProfile) return false;
      const source = /^[a-z0-9_.:-]{1,64}$/i.test(capture.source)
        ? capture.source
        : "identify_api";
      const configuredPurposes = new Set(config.identityCapture.purposes);
      const purposes = (capture.purposes ?? config.identityCapture.purposes)
        .filter((purpose) => configuredPurposes.has(purpose))
        .slice(0, 3);
      if (purposes.length === 0) return false;
      const ids = getIds();
      const payload = {
        siteKey: config.siteKey,
        occurredAt: now(),
        source,
        purposes,
        visitor: { anonId: ids.anonId, sessionId: ids.sessionId },
        identity: { email, phone, externalId },
        ...(hasProfile ? { profile } : {}),
        consent: {
          state: consentSnapshot.state,
          gpc: consentSnapshot.gpc,
          dnt: consentSnapshot.dnt,
          ...(consentSnapshot.cmp
            ? {
                cmp: {
                  source: consentSnapshot.cmp.source,
                  tcString: consentSnapshot.cmp.tcString,
                  uspString: consentSnapshot.cmp.uspString,
                  gppString: consentSnapshot.cmp.gppString,
                  gppSectionIds: consentSnapshot.cmp.gppSectionIds,
                },
              }
            : {}),
        },
      };
      const endpoint = config.identityCapture.endpointPath || "/pxl/identity";
      const url = `${config.apiBase.replace(/\/+$/, "")}${endpoint}`;
      return postWithRetry(url, payload);
    };

    const recordConsentEvidence = (
      next: ConsentSnapshot,
      signalSource?: string,
    ) => {
      const evidence = buildConsentEvidence(next, signalSource);
      if (!evidence) return;
      if (storage.local.get(consentEvidenceKey) === evidence.fingerprint) {
        return;
      }
      storage.local.set(consentEvidenceKey, evidence.fingerprint);
      trackEvent("consent_update", evidence.payload);
    };

    const setConsent = async (
      next: ConsentSnapshot,
      emitEvent = true,
      signalSource?: string,
    ) => {
      consentSnapshot = next;
      storage.local.set(
        consentKey,
        JSON.stringify({ state: next.state, updatedAt: now() }),
      );
      bus.emit("consent", consentSnapshot);
      window.dispatchEvent(
        new CustomEvent("prospix:consent-changed", {
          detail: {
            state: consentSnapshot.state,
            gpc: consentSnapshot.gpc,
            dnt: consentSnapshot.dnt,
          },
        }),
      );
      if (!canTrack("analytics")) {
        // Drop queued non-essential events when tracking is not permitted,
        // but preserve essential events (e.g., consent_update, pixel_error).
        const drained = queue.drain();
        drained.forEach((event) => {
          if (!isEssentialEvent(event.type)) return;
          queue.enqueue(event);
        });
      }
      if (emitEvent) recordConsentEvidence(next, signalSource);
    };

    const refreshConsent = async () => {
      const resolved = await resolveConsent(config, readConsentFromStorage());
      await setConsent(resolved);
    };

    type FlushOptions = { useBeacon?: boolean; reason?: string };
    let flushInFlight = false;
    let flushQueued: FlushOptions | undefined;

    // Build an envelope and deliver via sendBeacon/fetch with bounded retries.
    const flushInternal = async ({
      useBeacon = false,
      reason = "timer",
    }: FlushOptions = {}) => {
      if (flags.killSwitch) return;
      if (storage.local.get("prospix_optout") === "1") {
        queue.clear();
        return;
      }
      const batch = queue.drain();
      if (!batch.length) return;

      const allowed = batch.filter((event) =>
        canTrack(isEssentialEvent(event.type) ? "essential" : "analytics"),
      );
      const dropped = batch.length - allowed.length;
      if (!allowed.length) return;
      if (dropped) {
        logger.log("events_dropped_no_consent", { dropped });
      }

      const hasNonEssential = allowed.some(
        (event) => !isEssentialEvent(event.type),
      );
      const hasConsentEvidence = allowed.some(
        (event) => event.type === "consent_update",
      );
      const shouldAttachVisitor = hasNonEssential || hasConsentEvidence;
      const ids = shouldAttachVisitor ? getIds() : undefined;
      const rawContext = buildContext(config, redactor.sanitizeUrl);
      const filteredContext = {
        ...(canTrackCategory("context_page") ? { page: rawContext.page } : {}),
        ...(canTrackCategory("context_device")
          ? { device: rawContext.device }
          : {}),
        ...(canTrackCategory("marketing_params")
          ? { marketing: rawContext.marketing }
          : {}),
        ...(canTrackCategory("network_hints")
          ? { network: rawContext.network }
          : {}),
      };
      const contextPayload =
        Object.keys(filteredContext).length > 0 ? filteredContext : undefined;
      const payload = {
        siteKey: config.siteKey,
        sdk: config.sdk,
        sentAt: now(),
        batchId: ulid(),
        consent: consentSnapshot,
        policy: {
          region: config.privacy.region,
          consentMode: resolveConsentMode(),
          dataCategories: buildCategoryPolicySnapshot(),
        },
        ...(shouldAttachVisitor && ids
          ? {
              visitor: { anonId: ids.anonId, sessionId: ids.sessionId },
            }
          : {}),
        ...(hasNonEssential && ids
          ? {
              ...(contextPayload ? { context: contextPayload } : {}),
            }
          : {}),
        events: allowed.map((event) => ({
          eventId: event.eventId,
          type: event.type,
          occurredAt: event.occurredAt,
          properties: event.props,
        })),
        reason,
      };

      const url = `${config.apiBase.replace(/\/+$/, "")}/pxl/ingest`;
      const ok = useBeacon
        ? await postJson(url, payload, { useBeacon })
        : { ok: await postWithRetry(url, payload) };

      if (!ok.ok) {
        allowed.forEach((event) => {
          if (event.attempts >= 2) return;
          queue.enqueue({
            ...event,
            attempts: event.attempts + 1,
          });
        });
      }
    };

    const flush = async (options: FlushOptions = {}) => {
      if (options.useBeacon) {
        try {
          await flushInternal(options);
        } catch (error) {
          logger.error("flush_beacon_failed", error);
        }
        return;
      }

      if (flushInFlight) {
        flushQueued = {
          reason: options.reason ?? "coalesced",
          useBeacon: Boolean(options.useBeacon || flushQueued?.useBeacon),
        };
        return;
      }

      flushInFlight = true;
      try {
        let nextFlush: FlushOptions | undefined = options;
        while (nextFlush) {
          const currentFlush = nextFlush;
          flushQueued = undefined;
          try {
            await flushInternal(currentFlush);
          } catch (error) {
            logger.error("flush_failed", error);
          }
          nextFlush = flushQueued;
        }
      } finally {
        flushInFlight = false;
      }
    };

    let flushTimer: ReturnType<typeof setInterval> | undefined;
    const queueFlushTimer = () => {
      if (flushTimer) return;
      flushTimer = setInterval(() => {
        void flush({ reason: "interval" });
      }, config.queue.flushIntervalMs);
    };

    const installFlushHooks = () => {
      const doc = safeDocument();
      if (!doc) return;
      doc.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
          void flush({ useBeacon: true, reason: "visibility" });
        }
      });
      window.addEventListener("pagehide", () => {
        void flush({ useBeacon: true, reason: "pagehide" });
      });
    };

    const loadPluginScript = (name: string, manifestData: PluginManifest) => {
      if (pluginFactories.has(name)) return Promise.resolve();
      const pending = pendingPluginLoads.get(name);
      if (pending) return pending.promise;

      const plugin = manifestData.plugins[name];
      if (!plugin) return Promise.resolve();

      const basePath =
        typeof manifestData.basePath === "string" &&
        manifestData.basePath.startsWith("/cdn/v1/")
          ? manifestData.basePath.replace(/\/+$/, "")
          : "/cdn/v1/plugins";

      const pluginPath = plugin.path;
      const pluginPathPattern = /^[a-z0-9_-]+(?:\/[a-z0-9_-]+)*\.js$/i;
      if (
        typeof pluginPath !== "string" ||
        pluginPath.startsWith("/") ||
        pluginPath.includes("\\\\") ||
        pluginPath.includes("..") ||
        !pluginPathPattern.test(pluginPath)
      ) {
        logger.warn("plugin_path_invalid", name, pluginPath);
        return Promise.resolve();
      }

      const srcUrl = new URL(`${basePath}/${pluginPath}`, scriptUrl.origin);
      if (
        srcUrl.origin !== scriptUrl.origin ||
        !srcUrl.pathname.startsWith("/cdn/v1/plugins/")
      ) {
        logger.warn("plugin_url_blocked", name, srcUrl.toString());
        return Promise.resolve();
      }

      let resolvePromise: () => void = () => {};
      const promise = new Promise<void>((resolve) => {
        resolvePromise = resolve;
      });
      pendingPluginLoads.set(name, { promise, resolve: resolvePromise });

      const script = document.createElement("script");
      script.async = true;
      script.src = srcUrl.toString();
      if (typeof plugin.integrity === "string" && plugin.integrity.trim()) {
        script.integrity = plugin.integrity;
        script.crossOrigin = "anonymous";
      }
      script.onload = () => {
        resolvePromise();
        pendingPluginLoads.delete(name);
      };
      script.onerror = () => {
        logger.warn("plugin_load_failed", name);
        resolvePromise();
        pendingPluginLoads.delete(name);
      };
      document.head.appendChild(script);

      return promise;
    };

    const resolvePluginOrder = (
      names: string[],
      manifestData: PluginManifest,
    ) => {
      const order: string[] = [];
      const visiting = new Set<string>();
      const visited = new Set<string>();

      const visit = (name: string) => {
        if (visited.has(name) || visiting.has(name)) return;
        visiting.add(name);
        const deps = manifestData.plugins[name]?.deps ?? [];
        deps.forEach(visit);
        visiting.delete(name);
        visited.add(name);
        order.push(name);
      };

      names.forEach(visit);
      return order;
    };

    const initPlugin = (name: string) => {
      const factory = pluginFactories.get(name);
      if (!factory) return;
      if (pluginInstances.has(name)) return;
      const ctx = {
        config,
        flags,
        siteKey: config.siteKey,
        get ids() {
          return getIds();
        },
        get consent() {
          return consentSnapshot;
        },
        LOG: logger.log,
        warn: logger.warn,
        error: logger.error,
        track: trackEvent,
        queue: { track: trackEvent },
        emit: (event: string, payload: unknown) =>
          bus.emit(event as any, payload as any),
        on: (event: string, handler: (payload: unknown) => void) =>
          bus.on(event as any, handler as any),
        transport: {
          postWithRetry: (url: string, payload: unknown) =>
            postWithRetry(url, payload),
        },
        redact: redactor,
        storage: storage.local,
        canTrack,
        canTrackCategory,
        captureIdentity,
      };
      const instance = factory(ctx) ?? {};
      pluginInstances.set(name, instance);
      try {
        instance.onInit?.();
      } catch {
        // ignore
      }
    };

    const essentialPlugins = new Set<string>(["consent", "gcm", "diagnostics"]);

    const resolvePluginTier = (name: string) => {
      if (essentialPlugins.has(name)) return "essential";
      const tiers = config.plugins.tiers ?? {};
      if (tiers.essential?.includes(name)) return "essential";
      if (tiers.early?.includes(name)) return "early";
      if (tiers.late?.includes(name)) return "late";
      if (tiers.idle?.includes(name)) return "idle";
      const manifestTier = manifest.plugins[name]?.tier;
      if (manifestTier) return manifestTier;
      return "early";
    };

    const resolveEnabledPlugins = (
      tiers: Set<"essential" | "early" | "late" | "idle">,
    ) => {
      const disabled = new Set([
        ...config.plugins.disabled,
        ...(flags.disabledPlugins ?? []),
      ]);

      const isMarketingBlocked = !canTrack("marketing");
      const requested = new Set<string>([
        ...manifest.defaults,
        ...(config.plugins.enabled ?? []),
      ]);

      const candidates = Array.from(requested)
        .filter((name) => !disabled.has(name))
        .filter((name) => Boolean(manifest.plugins[name]))
        .filter((name) => name !== "diagnostics" || config.debug);

      const isMarketingPlugin = (name: string) =>
        config.privacy.marketingPlugins.includes(name) ||
        manifest.plugins[name]?.kind === "marketing";

      const filtered = isMarketingBlocked
        ? candidates.filter((name) => !isMarketingPlugin(name))
        : candidates;

      return filtered.filter((name) => tiers.has(resolvePluginTier(name)));
    };

    const unloadNonEssentialPlugins = () => {
      const keep = new Set(essentialPlugins);
      if (config.debug) keep.add("diagnostics");
      for (const [name, instance] of Array.from(pluginInstances.entries())) {
        if (keep.has(name)) continue;
        try {
          instance.onUnload?.();
        } catch {
          // ignore
        }
        pluginInstances.delete(name);
      }
    };

    const loadedTiers = new Set<string>();
    const loadPluginsInternal = async (
      tiers: Set<"essential" | "early" | "late" | "idle">,
    ) => {
      if (storage.local.get("prospix_optout") === "1") return;
      const next = Array.from(tiers).filter((tier) => !loadedTiers.has(tier));
      if (!next.length) return;

      const enabled = resolveEnabledPlugins(new Set(next));
      const order = resolvePluginOrder(enabled, manifest);
      const readyQueue: string[] = [];

      for (const name of order) {
        const hadInstance = pluginInstances.has(name);
        await loadPluginScript(name, manifest);
        initPlugin(name);
        if (!hadInstance && pluginInstances.has(name)) {
          readyQueue.push(name);
        }
      }

      readyQueue.forEach((name) => {
        try {
          pluginInstances.get(name)?.onReady?.();
        } catch {
          // ignore
        }
      });

      next.forEach((tier) => loadedTiers.add(tier));
    };

    let pluginWorkQueue: Promise<void> = Promise.resolve();
    const enqueuePluginWork = (work: () => Promise<void> | void) => {
      pluginWorkQueue = pluginWorkQueue.then(async () => {
        try {
          await work();
        } catch {
          // ignore
        }
      });
      return pluginWorkQueue;
    };

    const loadPlugins = (tiers: Set<"essential" | "early" | "late" | "idle">) =>
      enqueuePluginWork(() => loadPluginsInternal(tiers));

    const unloadPlugins = () =>
      enqueuePluginWork(() => {
        unloadNonEssentialPlugins();
        loadedTiers.forEach((tier) => {
          if (tier !== "essential") loadedTiers.delete(tier);
        });
      });

    let initialized = false;
    let initializationSuppressedByOptout = false;
    let initPromise: Promise<void> | undefined;
    const init = (override?: Partial<PixelConfig>) => {
      if (initialized) return Promise.resolve();
      if (initPromise) return initPromise;
      initPromise = (async () => {
        config = resolveConfig(scriptUrl, override);
        config.consentMode = normalizeConsentMode(config.consentMode);
        logger.log("pixel_init", {
          siteKey: config.siteKey,
          apiBase: config.apiBase,
        });

        const [defaults, fetchedFlags, fetchedManifest] = await Promise.all([
          loadDefaults(scriptUrl),
          loadFlags(scriptUrl),
          loadManifest(scriptUrl),
        ]);

        config = resolveConfig(
          scriptUrl,
          deepMerge(defaults as PixelConfig, override ?? {}),
        );
        config.consentMode = normalizeConsentMode(config.consentMode);
        if (config.features) {
          config = applyFeatureFlags(config, config.features);
        }
        flags = fetchedFlags;
        manifest = fetchedManifest;
        redactor = createRedactor({
          redactKeys: config.privacy.redactKeys,
          allowedQueryParams: config.privacy.allowedQueryParams,
        });

        const remoteConfig = await loadRemoteConfig(
          config.apiBase,
          config.siteKey,
        );
        if (remoteConfig) {
          const remoteOverrides: Record<string, unknown> = {};
          if (remoteConfig.features) {
            remoteOverrides.features = remoteConfig.features;
          }
          if (remoteConfig.platform) {
            remoteOverrides.platform = remoteConfig.platform;
          }
          if (remoteConfig.plugins) {
            remoteOverrides.plugins = remoteConfig.plugins;
          }
          if (remoteConfig.destinations) {
            remoteOverrides.destinations = remoteConfig.destinations;
          }
          if (remoteConfig.consentMode) {
            remoteOverrides.consentMode = remoteConfig.consentMode;
          }
          if (remoteConfig.privacy) {
            remoteOverrides.privacy = remoteConfig.privacy;
          }
          if (remoteConfig.identityCapture) {
            remoteOverrides.identityCapture = remoteConfig.identityCapture;
          }
          if (remoteConfig.intelligence) {
            remoteOverrides.intelligence = remoteConfig.intelligence;
          }
          if (Object.keys(remoteOverrides).length > 0) {
            config = resolveConfig(
              scriptUrl,
              deepMerge(config as PixelConfig, remoteOverrides),
            );
            config.consentMode = normalizeConsentMode(config.consentMode);
          }
        }
        if (config.features) {
          config = applyFeatureFlags(config, config.features);
        }
        queue = createQueue(storage.local, {
          key: `prospix_queue_${config.siteKey || "default"}`,
          maxEvents: config.queue.maxEvents,
          maxBytes: config.queue.maxBytes,
          ttlMs: config.queue.ttlMs,
        });

        await refreshConsent();
        if (storage.local.get("prospix_optout") === "1") {
          queue.clear();
          initializationSuppressedByOptout = true;
          initialized = true;
          return;
        }
        queueFlushTimer();
        installFlushHooks();
        const loadEssential = () =>
          loadPlugins(new Set(["essential"] as const));
        let nonEssentialScheduled = false;
        const scheduleNonEssentialLoads = () => {
          if (!canTrack("analytics")) return;
          const doc = safeDocument();
          const loadEarly = () => loadPlugins(new Set(["early"] as const));
          const loadLate = () => loadPlugins(new Set(["late"] as const));
          const loadIdle = () => loadPlugins(new Set(["idle"] as const));

          if (doc && doc.readyState === "loading") {
            if (!nonEssentialScheduled) {
              doc.addEventListener("DOMContentLoaded", loadEarly, {
                once: true,
              });
            }
          } else {
            loadEarly();
          }

          if (doc && doc.readyState === "complete") {
            loadLate();
          } else if (!nonEssentialScheduled) {
            window.addEventListener("load", loadLate, { once: true });
          }

          const idle = (window as any).requestIdleCallback as
            | ((cb: () => void, opts?: { timeout: number }) => void)
            | undefined;
          if (typeof idle === "function") {
            idle(loadIdle, { timeout: 3000 });
          } else {
            setTimeout(loadIdle, 3000);
          }
          nonEssentialScheduled = true;
        };

        void loadEssential();
        scheduleNonEssentialLoads();

        // Consent-driven plugin gating for opt-in/strict modes.
        bus.on("consent", () => {
          if (storage.local.get("prospix_optout") === "1") return;
          if (canTrack("analytics")) {
            scheduleNonEssentialLoads();
          } else {
            void unloadPlugins();
          }
        });
        initializationSuppressedByOptout = false;
        initialized = true;
      })().catch((error) => {
        initPromise = undefined;
        throw error;
      });
      return initPromise;
    };

    const api = (win as any).prospix || {};
    const queueCalls: unknown[] = Array.isArray(api.q) ? api.q : [];

    api.q = queueCalls;
    api.init = (override?: Partial<PixelConfig>) => init(override);
    api.track = (type: string, props?: Record<string, unknown>) =>
      trackEvent(type, props);
    api.captureIdentity = async (capture: FirstPartyIdentityCapture) => {
      await init();
      return captureIdentity(capture);
    };
    api.identify = async (traits: Record<string, unknown> = {}) => {
      await init();
      if (!canTrack("marketing")) return;
      if (!canTrackCategory("identity_linkage")) return;
      const traitString = (...keys: string[]) => {
        for (const key of keys) {
          const value = traits[key];
          if (typeof value === "string" && value.trim()) return value;
        }
        return undefined;
      };
      const rawEmail = traitString("email", "emailAddress", "email_address");
      const rawPhone = traitString("phone", "phoneNumber", "phone_number");
      const rawExternalId = traitString(
        "externalId",
        "external_id",
        "userId",
        "user_id",
        "customerId",
        "customer_id",
        "accountId",
        "account_id",
      );
      await captureIdentity({
        source: "identify_api",
        email: rawEmail,
        phone: rawPhone,
        externalId: rawExternalId,
        profile: {
          first: traitString("first", "firstName", "first_name"),
          middle: traitString("middle", "middleName", "middle_name"),
          last: traitString("last", "lastName", "last_name"),
          street: traitString("street", "address", "address1"),
          city: traitString("city"),
          state: traitString("state", "province"),
          postalCode: traitString("postalCode", "postal_code", "zip"),
          country: traitString("country", "countryCode", "country_code"),
        },
      });
      const allowlist = new Set(
        config.privacy.identifyAllowlist.map((key) => key.toLowerCase()),
      );
      const protectedTraitKeys = new Set([
        "email",
        "emailaddress",
        "email_address",
        "phone",
        "phonenumber",
        "phone_number",
        "externalid",
        "external_id",
        "userid",
        "user_id",
        "customerid",
        "customer_id",
        "accountid",
        "account_id",
        "first",
        "firstname",
        "first_name",
        "middle",
        "middlename",
        "middle_name",
        "last",
        "lastname",
        "last_name",
        "street",
        "address",
        "address1",
        "city",
        "state",
        "province",
        "postalcode",
        "postal_code",
        "zip",
        "country",
        "countrycode",
        "country_code",
      ]);
      const out: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(traits)) {
        if (protectedTraitKeys.has(key.toLowerCase())) continue;
        if (allowlist.has(key.toLowerCase())) {
          out[key] = redactor.sanitizeValue(value);
        }
      }
      const hasCrypto =
        typeof crypto !== "undefined" &&
        crypto.subtle &&
        typeof TextEncoder !== "undefined";
      if (hasCrypto && rawEmail) {
        const normalized = rawEmail.trim().toLowerCase();
        const digest = await crypto.subtle.digest(
          "SHA-256",
          new TextEncoder().encode(normalized),
        );
        out.email_hash = Array.from(new Uint8Array(digest))
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("");
      }
      if (hasCrypto && rawPhone) {
        const normalized = rawPhone.replace(/[^0-9+]/g, "");
        const digest = await crypto.subtle.digest(
          "SHA-256",
          new TextEncoder().encode(normalized),
        );
        out.phone_hash = Array.from(new Uint8Array(digest))
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("");
      }
      if (hasCrypto && rawExternalId) {
        const digest = await crypto.subtle.digest(
          "SHA-256",
          new TextEncoder().encode(rawExternalId.trim()),
        );
        out.external_id_hash = Array.from(new Uint8Array(digest))
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("");
      }
      trackEvent("identify", out);
    };
    api.resolveIdentity = async (match: IdentityResolutionInput) => {
      await init();
      if (config.features?.identityResolution === false) return;
      if (!canTrack("marketing")) return;
      if (!canTrackCategory("identity_resolution")) return;
      const normalized = normalizeIdentityResolution(match);
      if (!normalized) return;
      if (!canAcceptProvider(config, normalized.provider)) return;
      trackEvent("identity_resolution", normalized);
    };
    api.recordDecision = async (decision: DecisionInput) => {
      await init();
      if (config.features?.decisionTrace === false) return;
      if (!canTrack("marketing")) return;
      if (!canTrackCategory("audience_decisioning")) return;
      const normalized = normalizeDecision(decision);
      if (!normalized) return;
      if (
        normalized.provider &&
        !canAcceptProvider(config, normalized.provider)
      ) {
        return;
      }
      trackEvent("decision_recorded", normalized);
    };
    api.recordExposure = async (exposure: ExposureInput) => {
      await init();
      if (config.features?.decisionTrace === false) return;
      if (!canTrack("marketing")) return;
      if (!canTrackCategory("audience_decisioning")) return;
      const normalized = normalizeExposure(exposure);
      if (!normalized) return;
      trackEvent("decision_exposure", normalized);
    };
    api.recordOutcome = async (outcome: OutcomeInput) => {
      await init();
      if (config.features?.decisionTrace === false) return;
      if (!canTrack("marketing")) return;
      if (!canTrackCategory("audience_decisioning")) return;
      const normalized = normalizeOutcome(outcome);
      if (!normalized) return;
      if (
        normalized.provider &&
        !canAcceptProvider(config, normalized.provider)
      ) {
        return;
      }
      trackEvent("decision_outcome", normalized);
    };
    api.consent = (payload: {
      state: ConsentSnapshot["state"];
      cmp?: ConsentSnapshot["cmp"];
      tcString?: string;
      uspString?: string;
      gppString?: string;
      gppSectionIds?: number[];
    }) => {
      const cmp =
        payload.cmp ??
        (payload.tcString ||
        payload.uspString ||
        payload.gppString ||
        payload.gppSectionIds
          ? {
              source: payload.tcString
                ? "tcf"
                : payload.gppString
                  ? "gpp"
                  : payload.uspString
                    ? "usp"
                    : "manual",
              tcString: payload.tcString,
              uspString: payload.uspString,
              gppString: payload.gppString,
              gppSectionIds: payload.gppSectionIds,
            }
          : undefined);
      void setConsent(
        {
          state: payload.state,
          gpc: consentSnapshot.gpc,
          dnt: consentSnapshot.dnt,
          cmp,
        },
        true,
        "manual",
      );
    };
    api.openConsent = () => {
      window.dispatchEvent(new CustomEvent("prospix:open-consent"));
    };
    api.optout = (enabled: boolean) => {
      if (enabled) {
        void (async () => {
          try {
            await setConsent(
              {
                state: "denied",
                gpc: consentSnapshot.gpc,
                dnt: consentSnapshot.dnt,
              },
              true,
              "optout",
            );
            await flush({ useBeacon: true, reason: "optout" });
          } finally {
            storage.local.set("prospix_optout", "1");
            queue.clear();
            await unloadPlugins();
          }
        })();
      } else {
        storage.local.remove("prospix_optout");
        if (initializationSuppressedByOptout) {
          initialized = false;
          initPromise = undefined;
          initializationSuppressedByOptout = false;
          void init().catch((error) =>
            logger.error("pixel_init_failed", error),
          );
        } else if (canTrack("analytics")) {
          void loadPlugins(new Set(["early", "late", "idle"] as const));
        }
      }
    };
    api.flush = (options?: { reason?: string }) =>
      flush({ useBeacon: false, reason: options?.reason ?? "manual" });
    api.getIds = () => getIds();
    api.getPlatformStatus = async () => {
      await init();
      return getPlatformStatus(config);
    };

    (win as any).prospix = api;

    // Drain calls queued by the loader shim.
    const processQueue = async () => {
      const initialCalls = queueCalls.splice(0);
      const queuedInit = initialCalls.find(
        (entry) =>
          (Array.isArray(entry) && entry[0] === "init") ||
          (entry &&
            typeof entry === "object" &&
            (entry as any).method === "init"),
      );
      const initOverride = Array.isArray(queuedInit)
        ? queuedInit[1]
        : queuedInit && typeof queuedInit === "object"
          ? (queuedInit as any).args?.[0]
          : undefined;
      try {
        await init(initOverride as Partial<PixelConfig> | undefined);
      } catch (error) {
        queueCalls.unshift(...initialCalls);
        throw error;
      }

      for (const entry of [...initialCalls, ...queueCalls.splice(0)]) {
        if (Array.isArray(entry)) {
          const [method, ...args] = entry;
          if (method === "init") continue;
          if (typeof (api as any)[method] === "function") {
            await (api as any)[method](...args);
          }
          continue;
        }
        if (entry && typeof entry === "object" && "method" in (entry as any)) {
          const method = (entry as any).method;
          const args = (entry as any).args ?? [];
          if (method === "init") continue;
          if (typeof (api as any)[method] === "function") {
            try {
              const result = await (api as any)[method](...args);
              if (typeof (entry as any).resolve === "function") {
                (entry as any).resolve(result);
              }
            } catch (error) {
              if (typeof (entry as any).reject === "function") {
                (entry as any).reject(error);
                continue;
              }
              throw error;
            }
          }
        }
      }
      api.q = [];
      api.ready = true;
      window.dispatchEvent(new Event("prospix:ready"));
    };

    void processQueue().catch((error) =>
      logger.error("pixel_init_failed", error),
    );
  } catch {
    // swallow errors
  }
})();
